// Journey amendment 2026-09-14 (R5a,).
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { updateWorkspaceProfile, markOnboardingComplete } from '@/lib/api';
import { invalidateMe, useMe } from '@/lib/auth/useMe';

export const WEBSITE_PRICE_WALL_URL: string =
  (import.meta.env.VITE_WEBSITE_PRICE_WALL_URL as string | undefined) || 'https://zoediagnostics.ai/sign-up/';

const IN_APP_SIGN_UP_ENVIRONMENTS = ['dev', 'staging', 'feedback'];

export function inAppSignUpEnabled(): boolean {
  return IN_APP_SIGN_UP_ENVIRONMENTS.includes(import.meta.env.VITE_APP_ENVIRONMENT ?? '');
}

export function signUpUrl(): string {
  return inAppSignUpEnabled() ? '/sign-up' : WEBSITE_PRICE_WALL_URL;
}

/** Leaves the app for the website price wall. */
export function WebsitePriceWallRedirect() {
  const inApp = inAppSignUpEnabled();
  useEffect(() => {
    if (!inApp) window.location.replace(WEBSITE_PRICE_WALL_URL);
  }, [inApp]);
  if (inApp) return <Navigate to="/sign-up" replace />;
  return <p role="status" style={{ padding: 24 }}>Taking you to checkout on zoediagnostics.ai… <a href={WEBSITE_PRICE_WALL_URL}>Continue</a></p>;
}

export function safeInternalNext(value: string | null): string | null {
  if (!value || !value.startsWith('/') || /^\/[/\\]/.test(value) || value.includes('\\')) return null;
  if (/^\/(onboarding|signin|sign-up|login|start|pay)(\/|\?|$)/.test(value)) return null;
  return value;
}

/** Marks a workspace's setup complete (investor mode unless already set). */
export async function completeInvestorSetup(mode: 'investor' | 'operator' | null | undefined): Promise<void> {
  if (!mode) await updateWorkspaceProfile({ mode: 'investor' });
  await markOnboardingComplete();
  invalidateMe();
}

/** Replaces every retired onboarding page. */
export function OnboardingRetiredRedirect() {
  const location = useLocation();
  const identity = useMe();
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const started = useRef(false);
  const next = safeInternalNext(new URLSearchParams(location.search).get('next')) ?? '/deal/new';

  useEffect(() => {
    if (identity.state !== 'ok' || started.current) return;
    if (identity.me?.workspace?.onboarding_completed_at) { setDone(true); return; }
    started.current = true;
    completeInvestorSetup(identity.me?.workspace?.mode)
      .then(() => setDone(true))
      .catch(() => setFailed(true));
  }, [identity.state, identity.me]);

  if (done) return <Navigate to={next} replace />;
  if (failed) return <p role="alert" style={{ padding: 24 }}>Your workspace could not be set up. Refresh to try again.</p>;
  return <p role="status" style={{ padding: 24 }}>Setting up your workspace…</p>;
}
